<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PipelineWebhookModal from './PipelineWebhookModal.vue';

defineProps({
  pipelines: {
    type: Array,
    default: () => [],
  },
});

const store = useStore();
const { showAlert } = useAlert();
const { t } = useI18n();

const showModal = ref(false);
const selectedWebhook = ref(null);
const testingId = ref(null);

const webhooks = computed(() => store.getters['pipelineWebhooks/allWebhooks']);
const uiFlags = computed(() => store.getters['pipelineWebhooks/uiFlags']);

onMounted(() => {
  store.dispatch('pipelineWebhooks/get');
});

const EVENT_META = {
  pipeline_card_created: {
    label: 'Created',
    classes: 'bg-success-subtle text-success',
    icon: 'i-lucide-plus-circle',
  },
  pipeline_card_updated: {
    label: 'Updated',
    classes: 'bg-n-blue-3 text-n-blue-11',
    icon: 'i-lucide-pencil-line',
  },
  pipeline_card_deleted: {
    label: 'Deleted',
    classes: 'bg-n-red-3 text-danger',
    icon: 'i-lucide-trash-2',
  },
  pipeline_card_stage_changed: {
    label: 'Stage Changed',
    classes: 'bg-n-yellow-3 text-n-yellow-11',
    icon: 'i-lucide-arrow-right-circle',
  },
  pipeline_card_won: {
    label: 'Won',
    classes: 'bg-success-subtle text-success',
    icon: 'i-lucide-trophy',
  },
  pipeline_card_lost: {
    label: 'Lost',
    classes: 'bg-n-red-3 text-danger',
    icon: 'i-lucide-circle-x',
  },
  pipeline_card_owner_changed: {
    label: 'Owner Changed',
    classes: 'bg-n-purple-3 text-n-purple-11',
    icon: 'i-lucide-user-round-cog',
  },
};

const getEventMeta = event =>
  EVENT_META[event] || {
    label: event,
    classes: 'bg-n-alpha-2 text-n-slate-11',
    icon: 'i-lucide-zap',
  };

const formatDate = dateStr => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const openCreateModal = () => {
  selectedWebhook.value = null;
  showModal.value = true;
};

const openEditModal = webhook => {
  selectedWebhook.value = webhook;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedWebhook.value = null;
};

const handleSaved = () => {
  closeModal();
  store.dispatch('pipelineWebhooks/get');
};

const handleTest = async webhook => {
  testingId.value = webhook.id;
  try {
    await store.dispatch('pipelineWebhooks/test', webhook.id);
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.TEST_SENT_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE_WEBHOOK.MESSAGES.TEST_FAILED'));
  } finally {
    testingId.value = null;
  }
};

const handleDelete = async webhook => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('PIPELINE_WEBHOOK.DELETE_CONFIRM', { name: webhook.name })
    )
  )
    return;
  try {
    await store.dispatch('pipelineWebhooks/delete', webhook.id);
    showAlert(t('PIPELINE_WEBHOOK.DELETE_SUCCESS'));
  } catch {
    showAlert(t('PIPELINE_WEBHOOK.DELETE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center justify-between pl-6 pr-14 py-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-8 h-8 rounded-lg bg-n-brand/10 flex items-center justify-center shrink-0"
        >
          <Icon icon="i-lucide-webhook" class="size-4 text-n-brand" />
        </div>
        <div>
          <h2 class="text-sm font-semibold text-n-slate-12 leading-none">
            {{ $t('PIPELINE_WEBHOOK.TITLE') }}
          </h2>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ $t('PIPELINE_WEBHOOK.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <Button size="sm" @click="openCreateModal">
        <template #icon>
          <Icon icon="i-lucide-plus" class="size-4" />
        </template>
        {{ $t('PIPELINE_WEBHOOK.CREATE_BUTTON') }}
      </Button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-5 py-4">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center gap-2 py-12"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-4 text-n-slate-11 animate-spin"
        />
        <span class="text-n-slate-11 text-sm">
          {{ $t('PIPELINE_WEBHOOK.LOADING') }}
        </span>
      </div>

      <!-- Empty state -->
      <div
        v-else-if="webhooks.length === 0"
        class="flex flex-col items-center justify-center py-14 gap-4"
      >
        <div
          class="w-14 h-14 rounded-2xl bg-n-alpha-2 flex items-center justify-center"
        >
          <Icon icon="i-lucide-webhook" class="size-7 text-n-slate-10" />
        </div>
        <div class="text-center">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-1 max-w-xs leading-relaxed">
            {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.DESCRIPTION') }}
          </p>
        </div>
        <Button size="sm" @click="openCreateModal">
          {{ $t('PIPELINE_WEBHOOK.EMPTY_STATE.CTA') }}
        </Button>
      </div>

      <!-- Webhook List -->
      <div v-else class="flex flex-col gap-2.5">
        <div
          v-for="webhook in webhooks"
          :key="webhook.id"
          class="group relative rounded-xl border bg-n-background transition-all duration-150 overflow-hidden"
          :class="
            webhook.active
              ? 'border-n-weak hover:border-n-strong hover:shadow-sm'
              : 'border-n-weaker opacity-70 hover:opacity-100'
          "
        >
          <!-- Active accent bar -->
          <div
            class="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-xl transition-colors"
            :class="webhook.active ? 'bg-success' : 'bg-n-weak'"
          />

          <div class="pl-5 pr-4 py-3.5">
            <div class="flex items-start justify-between gap-3">
              <!-- Main info -->
              <div class="flex-1 min-w-0">
                <!-- Name + status -->
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-sm font-semibold text-n-slate-12 truncate">
                    {{ webhook.name }}
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium shrink-0"
                    :class="
                      webhook.active
                        ? 'bg-success-subtle text-success'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span
                      class="size-1.5 rounded-full"
                      :class="webhook.active ? 'bg-success' : 'bg-n-slate-9'"
                    />
                    {{
                      webhook.active
                        ? $t('PIPELINE_WEBHOOK.STATUS.ACTIVE')
                        : $t('PIPELINE_WEBHOOK.STATUS.INACTIVE')
                    }}
                  </span>
                </div>

                <!-- URL -->
                <p
                  class="text-xs text-n-slate-10 truncate font-mono leading-none mb-2.5"
                >
                  {{ webhook.url }}
                </p>

                <!-- Event badges -->
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="event in webhook.events"
                    :key="event"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium"
                    :class="getEventMeta(event).classes"
                  >
                    <Icon
                      :icon="getEventMeta(event).icon"
                      class="size-3 shrink-0"
                    />
                    {{ getEventMeta(event).label }}
                  </span>
                </div>

                <!-- Last triggered -->
                <div
                  v-if="webhook.last_triggered_at"
                  class="flex items-center gap-1.5 mt-2.5 text-xs text-n-slate-10"
                >
                  <Icon icon="i-lucide-clock-3" class="size-3 shrink-0" />
                  <span>
                    {{ $t('PIPELINE_WEBHOOK.LAST_TRIGGERED') }}
                    {{ formatDate(webhook.last_triggered_at) }}
                  </span>
                  <span
                    v-if="webhook.last_status_code"
                    class="inline-flex items-center px-1.5 py-px rounded font-mono font-medium text-xs"
                    :class="
                      webhook.last_status_code < 300
                        ? 'bg-success-subtle text-success'
                        : 'bg-n-red-3 text-danger'
                    "
                  >
                    {{ webhook.last_status_code }}
                  </span>
                </div>
              </div>

              <!-- Actions — visible on hover -->
              <div
                class="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-100"
              >
                <button
                  class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  :title="$t('PIPELINE_WEBHOOK.ACTIONS.TEST')"
                  :disabled="testingId === webhook.id"
                  @click="handleTest(webhook)"
                >
                  <Icon
                    :icon="
                      testingId === webhook.id
                        ? 'i-lucide-loader-circle'
                        : 'i-lucide-send'
                    "
                    class="size-3.5"
                    :class="{ 'animate-spin': testingId === webhook.id }"
                  />
                </button>
                <button
                  class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  :title="$t('PIPELINE_WEBHOOK.ACTIONS.EDIT')"
                  @click="openEditModal(webhook)"
                >
                  <Icon icon="i-lucide-pencil" class="size-3.5" />
                </button>
                <button
                  class="p-1.5 rounded-lg text-n-slate-10 hover:text-danger hover:bg-n-red-3 transition-colors"
                  :title="$t('PIPELINE_WEBHOOK.ACTIONS.DELETE')"
                  @click="handleDelete(webhook)"
                >
                  <Icon icon="i-lucide-trash-2" class="size-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <PipelineWebhookModal
      v-if="showModal"
      :webhook="selectedWebhook"
      :pipelines="pipelines"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>
